import React from 'react';
import { 
  FaMapMarkerAlt, 
  FaBriefcase, 
  FaDollarSign, 
  FaClock,
  FaGraduationCap,
  FaIndustry,
  FaUserTie,
  FaCalendar,
  FaVenusMars,
  FaGlobe
} from 'react-icons/fa';
import './JobOverview.css';

const JobOverview = ({ job }) => {
  const formatSalary = (salary) => {
    if (!salary) return 'Not specified';
    
    const { min, max, currency, period } = salary;
    const currencySymbol = currency === 'USD' ? '$' : currency;
    const periodText = period === 'yearly' ? 'year' : period === 'monthly' ? 'month' : 'hour';
    
    if (min && max) {
      return `${currencySymbol}${min.toLocaleString()} - ${currencySymbol}${max.toLocaleString()} per ${periodText}`;
    } else if (min) {
      return `${currencySymbol}${min.toLocaleString()} per ${periodText}`;
    } else if (max) {
      return `Up to ${currencySymbol}${max.toLocaleString()} per ${periodText}`;
    }
    
    return 'Not specified';
  };

  const formatLocation = (location) => {
    if (!location) return 'Not specified';
    
    if (location.type === 'remote') {
      return 'Remote';
    }
    
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    if (location.country) parts.push(location.country);
    
    if (parts.length === 0 && location.address) {
      return location.address;
    }
    
    return parts.join(', ') || 'Not specified';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDeadlineStatus = (deadlineString) => {
    if (!deadlineString) return '';
    
    const deadline = new Date(deadlineString);
    const now = new Date();
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return 'deadline-expired';
    } else if (daysLeft <= 3) {
      return 'deadline-near';
    }
    return '';
  };

  const overviewItems = [
    {
      icon: <FaCalendar />,
      title: 'Posted Date',
      value: formatDate(job.datePosted || job.postedDate),
      key: 'posted'
    },
    {
      icon: <FaClock />,
      title: 'Application Deadline',
      value: formatDate(job.applicationDeadline || job.validThrough),
      className: getDeadlineStatus(job.applicationDeadline || job.validThrough),
      key: 'deadline'
    },
    {
      icon: <FaIndustry />,
      title: 'Industry',
      value: job.overview?.industry || job.category || 'Not specified',
      key: 'industry'
    },
    {
      icon: <FaBriefcase />,
      title: 'Job Type',
      value: job.jobType ? job.jobType.replace('_', ' ').toUpperCase() : 'Not specified',
      key: 'jobType'
    },
    {
      icon: <FaUserTie />,
      title: 'Experience',
      value: job.overview?.experience || job.experienceRequirements || 'Not specified',
      key: 'experience'
    },
    {
      icon: <FaGraduationCap />,
      title: 'Qualification',
      value: job.overview?.qualification || job.educationRequirements || 'Not specified',
      key: 'qualification'
    },
    {
      icon: <FaVenusMars />,
      title: 'Gender',
      value: job.gender === 'both' ? 'All' : job.gender || 'All',
      key: 'gender'
    },
    {
      icon: <FaUserTie />,
      title: 'Career Level',
      value: job.overview?.careerLevel || 'Not specified',
      key: 'careerLevel'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Location',
      value: formatLocation(job.location),
      key: 'location'
    },
    {
      icon: <FaDollarSign />,
      title: 'Salary',
      value: formatSalary(job.salary),
      key: 'salary'
    }
  ];

  return (
    <div className="job-overview">
      <h3>Job Overview</h3>
      <div className="overview-items">
        {overviewItems.map(item => (
          <div key={item.key} className={`overview-item ${item.className || ''}`}>
            <div className="overview-icon">
              {item.icon}
            </div>
            <div className="overview-content">
              <h4>{item.title}</h4>
              <p>{item.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobOverview; 